/**
 * GazeHUD — Heads-up display overlay for gaze tracking controls.
 * Shows tracking status, scroll progress, and settings access.
 */

import { useState } from 'react'
import { useStore } from '@/store'
import { gazeTracker } from '@/lib/gaze-tracker'
import { gazeBridge } from '@/lib/gaze-bridge'
import { SettingsPanel } from '@/components/SettingsPanel'

export function GazeHUD() {
  const trackingMode = useStore((s) => s.trackingMode)
  const setTrackingMode = useStore((s) => s.setTrackingMode)
  const setGazeState = useStore((s) => s.setGazeState)
  const setMode = useStore((s) => s.setMode)
  const setDocument = useStore((s) => s.setDocument)
  const document = useStore((s) => s.document)
  const settings = useStore((s) => s.settings)
  const scrollY = useStore((s) => s.scrollY)
  const totalHeight = useStore((s) => s.totalHeight)
  const [starting, setStarting] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [webcamError, setWebcamError] = useState<string | null>(null)

  const clearGaze = () => {
    gazeBridge.clear()
    setGazeState({
      currentPoint: null, fixationLine: null, fixationParagraph: null,
      dwellStartTime: null, isFixating: false, velocity: 0,
    })
  }

  const handleToggleTracking = async () => {
    if (trackingMode !== 'off') {
      gazeTracker.stop()
      setTrackingMode('off')
      clearGaze()
      return
    }
  }

  const handleStartWebcam = async () => {
    setWebcamError(null)
    setStarting(true)

    // Check HTTPS before even trying
    const isSecure = location.protocol === 'https:' ||
      location.hostname === 'localhost' ||
      location.hostname === '127.0.0.1'

    if (!isSecure) {
      setWebcamError('Eye tracking requires HTTPS. Use mouse tracking for now, or access GazeWeave over HTTPS.')
      // No callback needed — gaze tracker writes to gazeBridge directly
      gazeTracker.startMouse(() => {})
      setTrackingMode('mouse')
      setStarting(false)
      return
    }

    const success = await gazeTracker.startWebcam(() => {})
    if (success) {
      setTrackingMode('webcam')
      // Go to calibration
      setMode('calibrating')
    } else {
      // Webcam failed, fall back to mouse
      setWebcamError('Webcam access failed. Falling back to mouse tracking.')
      gazeTracker.startMouse(() => {})
      setTrackingMode('mouse')
    }
    setStarting(false)
  }

  const handleStartMouse = () => {
    // No callback needed — gaze tracker writes to gazeBridge directly
    gazeTracker.startMouse(() => {})
    setTrackingMode('mouse')
  }

  const handleBack = () => {
    gazeTracker.stop()
    setTrackingMode('off')
    setDocument(null)
  }

  // Scroll progress (0-100)
  const scrollProgress = totalHeight > 0
    ? Math.min(100, Math.round((scrollY / Math.max(1, totalHeight - 600)) * 100))
    : 0

  // Reading time estimate
  const wordCount = document?.paragraphs.reduce((sum, p) => sum + p.text.split(/\s+/).length, 0) ?? 0
  const readingMinutes = Math.ceil(wordCount / 230) // avg reading speed

  const trackingLabel = trackingMode === 'webcam' ? '👁️ Webcam'
    : trackingMode === 'mouse' ? '🖱️ Mouse'
    : '⏸️ Off'

  const hudBg = settings.theme === 'dark'
    ? 'rgba(15,23,42,0.92)'
    : settings.theme === 'sepia'
    ? 'rgba(245,240,232,0.92)'
    : 'rgba(255,255,255,0.92)'

  const hudText = settings.theme === 'dark' ? '#94a3b8' : '#64748b'
  const hudBorder = settings.theme === 'dark' ? '#334155' : '#e2e8f0'
  const accentColor = settings.theme === 'dark' ? '#38bdf8'
    : settings.theme === 'sepia' ? '#d97706' : '#2563eb'

  return (
    <>
      {/* Top bar */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 16px',
        background: hudBg,
        backdropFilter: 'blur(8px)',
        borderBottom: `1px solid ${hudBorder}`,
        zIndex: 100,
      }}>
        {/* Left: back + title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
          <button onClick={handleBack} style={{
            padding: '5px 10px', borderRadius: '6px',
            border: `1px solid ${hudBorder}`, background: 'transparent',
            color: hudText, fontSize: '13px', cursor: 'pointer', flexShrink: 0,
          }}>
            ←
          </button>
          <span style={{
            color: hudText, fontSize: '14px',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {document?.title}
          </span>
        </div>

        {/* Center: progress */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          fontSize: '12px', color: hudText,
        }}>
          <span>{scrollProgress}%</span>
          <span style={{ opacity: 0.4 }}>·</span>
          <span>{readingMinutes} min read</span>
        </div>

        {/* Right: controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }}>
          {/* Separate webcam and mouse buttons */}
          {trackingMode === 'off' ? (
            <>
              <button onClick={handleStartWebcam} disabled={starting} style={{
                padding: '5px 10px', borderRadius: '6px', border: `1px solid ${hudBorder}`,
                background: 'transparent', color: hudText,
                fontSize: '12px', fontWeight: 500, cursor: 'pointer',
                opacity: starting ? 0.5 : 1,
              }}>
                {starting ? '⏳...' : '👁️ Webcam'}
              </button>
              <button onClick={handleStartMouse} style={{
                padding: '5px 10px', borderRadius: '6px', border: `1px solid ${hudBorder}`,
                background: 'transparent', color: hudText,
                fontSize: '12px', fontWeight: 500, cursor: 'pointer',
              }}>
                🖱️ Mouse
              </button>
            </>
          ) : (
            <button onClick={handleToggleTracking} style={{
              padding: '5px 12px', borderRadius: '6px', border: `1px solid ${hudBorder}`,
              background: accentColor, color: '#fff',
              fontSize: '12px', fontWeight: 500, cursor: 'pointer',
            }}>
              {trackingLabel} ✕
            </button>
          )}

          <button onClick={() => setSettingsOpen(true)} style={{
            padding: '5px 10px', borderRadius: '6px',
            border: `1px solid ${hudBorder}`, background: 'transparent',
            color: hudText, fontSize: '14px', cursor: 'pointer',
          }}>
            ⚙
          </button>
        </div>
      </div>

      {/* Scroll progress bar */}
      <div style={{
        position: 'fixed', top: '41px', left: 0, right: 0,
        height: '2px', background: hudBorder, zIndex: 100,
      }}>
        <div style={{
          height: '100%', width: `${scrollProgress}%`,
          background: accentColor,
          transition: 'width 0.3s ease-out',
        }} />
      </div>

      {/* Webcam error toast */}
      {webcamError && (
        <div style={{
          position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
          background: settings.theme === 'dark' ? '#1e293b' : '#1e293b',
          color: '#f8fafc', padding: '12px 20px', borderRadius: '10px',
          fontSize: '13px', maxWidth: '400px', textAlign: 'center',
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)', zIndex: 200,
          display: 'flex', alignItems: 'center', gap: '10px',
        }}>
          <span>⚠️ {webcamError}</span>
          <button onClick={() => setWebcamError(null)} style={{
            background: 'transparent', border: 'none', color: '#94a3b8',
            cursor: 'pointer', fontSize: '16px', padding: '0 2px',
          }}>✕</button>
        </div>
      )}

      {/* Settings panel */}
      <SettingsPanel open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </>
  )
}
